import { logger } from '../utils/logger';

/**
 * Knowledge Base result interface
 */
export interface KnowledgeBaseResult {
    content: string;
    metadata?: {
        sources?: Array<{
            title: string;
            similarity?: number;
            kb_id?: string | number;
        }>;
    };
}

/**
 * Knowledge Base by type result interface
 */
export interface KnowledgeBaseByTypeResult {
    content: string;
    nodes?: any[];
    count?: number;
}

/**
 * Combined knowledge base context result
 */
export interface CombinedKnowledgeBaseResult {
    globalContext: string;
    userContext: string;
    queryContext: string;
    combinedContext: string;
}

/**
 * Get relevant knowledge base context for a user prompt (semantic search only - query type)
 * Calls the registered 'knowledge-base.query' collection
 *
 * @param prompt - The user's question/prompt
 * @param collections - The collection registry containing knowledge-base.query
 * @param topK - Number of top results to retrieve (default: 1)
 * @returns Formatted knowledge base context string, or empty string if collection not registered
 */
const getKnowledgeBase = async ({
    prompt,
    collections,
    topK = 1
}: {
    prompt: string;
    collections?: any;
    topK?: number;
}): Promise<string> => {
    try {
        // Check if knowledge-base collection is registered
        if (!collections || !collections['knowledge-base'] || !collections['knowledge-base']['query']) {
            logger.warn('[KnowledgeBase] knowledge-base.query collection not registered, skipping');
            return '';
        }


        // Call the registered knowledge-base.query collection
        const result: KnowledgeBaseResult = await collections['knowledge-base']['query']({
            prompt,
            topK
        });

        if (!result || !result.content) {
            logger.warn('[KnowledgeBase] No knowledge base results returned');
            return '';
        }

        logger.info(`[KnowledgeBase] Retrieved knowledge base context (${result.content.length} chars)`);

        // Log sources if available
        if (result.metadata?.sources && result.metadata.sources.length > 0) {
            logger.warn(`[KnowledgeBase] Sources: ${result.metadata.sources.map(s => s.title).join(', ')}`);
        }

        return result.content;
    } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        logger.warn(`[KnowledgeBase] Error querying knowledge base: ${errorMsg}`);
        // Return empty string on error - don't fail the entire request
        return '';
    }
};

/**
 * Get global knowledge base nodes (type='global')
 * These are always included in system prompts for all users
 *
 * @param collections - The collection registry containing knowledge-base.getGlobal
 * @param limit - Maximum number of nodes to retrieve (default: 100)
 * @returns Formatted global knowledge base context string
 */
const getGlobalKnowledgeBase = async ({
    collections,
    limit = 100
}: {
    collections?: any;
    limit?: number;
}): Promise<string> => {
    try {
        // Check if knowledge-base.getGlobal collection is registered
        if (!collections || !collections['knowledge-base'] || !collections['knowledge-base']['getGlobal']) {
            logger.warn('[KnowledgeBase] knowledge-base.getGlobal collection not registered, skipping');
            return '';
        }


        const result: KnowledgeBaseByTypeResult = await collections['knowledge-base']['getGlobal']({ limit });

        if (!result || !result.content) {
            logger.warn('[KnowledgeBase] No global knowledge base nodes found');
            return '';
        }

        logger.info(`[KnowledgeBase] Retrieved ${result.count || 0} global knowledge base nodes`);
        return result.content;
    } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        logger.warn(`[KnowledgeBase] Error fetching global knowledge base: ${errorMsg}`);
        return '';
    }
};

/**
 * Get user-specific knowledge base nodes (type='user')
 * These are included only for the specific user based on createdBy field
 *
 * @param collections - The collection registry containing knowledge-base.getByUser
 * @param userId - The user ID to get nodes for
 * @param limit - Maximum number of nodes to retrieve (default: 100)
 * @returns Formatted user knowledge base context string
 */
const getUserKnowledgeBase = async ({
    collections,
    userId,
    limit = 100
}: {
    collections?: any;
    userId?: string | number;
    limit?: number;
}): Promise<string> => {
    try {
        if (!userId) {
            logger.warn('[KnowledgeBase] No userId provided, skipping user knowledge base');
            return '';
        }

        // Check if knowledge-base.getByUser collection is registered
        if (!collections || !collections['knowledge-base'] || !collections['knowledge-base']['getByUser']) {
            logger.warn('[KnowledgeBase] knowledge-base.getByUser collection not registered, skipping');
            return '';
        }


        const result: KnowledgeBaseByTypeResult = await collections['knowledge-base']['getByUser']({
            userId: String(userId),
            limit
        });

        if (!result || !result.content) {
            logger.info(`[KnowledgeBase] No user knowledge base nodes found for userId: ${userId}`);
            return '';
        }

        logger.info(`[KnowledgeBase] Retrieved ${result.count || 0} user knowledge base nodes for userId: ${userId}`);
        return result.content;
    } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        logger.warn(`[KnowledgeBase] Error fetching user knowledge base: ${errorMsg}`);
        return '';
    }
};

/**
 * Get all knowledge base context combining global, user, and query-based nodes
 * - Global nodes: Always included for all users
 * - User nodes: Included for the specific user based on createdBy
 * - Query nodes: Semantically searched based on the user prompt
 *
 * @param prompt - The user's question/prompt (for semantic search)
 * @param collections - The collection registry
 * @param userId - The user ID (for user-specific nodes)
 * @param topK - Number of top semantic search results (default: 3)
 * @returns Combined knowledge base context object with all types
 */
const getAllKnowledgeBase = async ({
    prompt,
    collections,
    userId,
    topK = 3
}: {
    prompt: string;
    collections?: any;
    userId?: string | number;
    topK?: number;
}): Promise<CombinedKnowledgeBaseResult> => {

    // Fetch all three types in parallel for better performance
    const [globalContext, userContext, queryContext] = await Promise.all([
        getGlobalKnowledgeBase({ collections }),
        getUserKnowledgeBase({ collections, userId }),
        getKnowledgeBase({ prompt, collections, topK })
    ]);

    // Combine all contexts with clear section headers
    let combinedContext = '';

    if (globalContext) {
        combinedContext += '## Global Knowledge Base\n';
        combinedContext += 'The following information applies to all queries:\n\n';
        combinedContext += globalContext + '\n\n';
    }

    if (userContext) {
        combinedContext += '## User-Specific Knowledge Base\n';
        combinedContext += 'The following information is specific to this user:\n\n';
        combinedContext += userContext + '\n\n';
    }

    if (queryContext) {
        combinedContext += '## Relevant Knowledge Base (Query-Matched)\n';
        combinedContext += 'The following information is semantically relevant to the current query:\n\n';
        combinedContext += queryContext + '\n\n';
    }

    return {
        globalContext,
        userContext,
        queryContext,
        combinedContext: combinedContext.trim()
    };
};

const KB = {
    getKnowledgeBase,
    getGlobalKnowledgeBase,
    getUserKnowledgeBase,
    getAllKnowledgeBase
};

export default KB;